// packages/web-ui/src/panels/file-viewer/FileEditorContext.ts
//
// Story W3-S3 AC #7 — React context shared between `<FileEditor>` (the CM6
// host) and the diagnostic-strip / popover renderers that need a handle to
// the current `EditorView` + the most recent diagnostics array.
//
// Kept TINY on purpose: the context's only role is the strip ↔ editor and
// popover ↔ editor channel. Everything else flows through props or the
// global zustand store.

import { createContext, useContext } from 'react';
import type { Diagnostic as CmDiagnostic } from '@codemirror/lint';
import type { EditorView } from '@codemirror/view';

export interface FileEditorContextValue {
  /** Most-recent diagnostics from the CM6 `linterField`. */
  diagnostics: CmDiagnostic[];
  /** Mutable ref to the live `EditorView`, or `null` while the editor is mounting. */
  view: EditorView | null;
}

export const DEFAULT_FILE_EDITOR_CONTEXT: FileEditorContextValue = {
  diagnostics: [],
  view: null,
};

export const FileEditorContext = createContext<FileEditorContextValue>(
  DEFAULT_FILE_EDITOR_CONTEXT,
);

/**
 * Hook accessor — returns the closest provider's value, or the default
 * (empty diagnostics + null view) when called outside a provider so the
 * `<DiagnosticStrip>` can render in isolation in tests / Storybook.
 */
export function useFileEditorContext(): FileEditorContextValue {
  return useContext(FileEditorContext);
}
